import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import WaveMark from "./WaveMark";
import { Btn } from "./Btn";

const LINKS = [
  { to: "/about", label: "About" },
  { to: "/work", label: "Work" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/process", label: "Process" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onDark = location.pathname === "/"; // hero is dark on home

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => (document.body.style.overflow = ""); }, [open]);

  const solid = scrolled || !onDark;
  const textCls = solid ? "text-ink" : "text-white";

  return (
    <>
      <header data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${solid ? "bg-paper-cream/90 backdrop-blur-xl border-b border-ink/10" : "bg-transparent"}`}>
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-5 md:px-12">
          <Link to="/" data-testid="nav-logo" className={`flex items-center gap-3 ${textCls}`}>
            <WaveMark width={42} stroke="currentColor" />
            <span className="leading-none">
              <span className="block font-display text-lg font-bold tracking-[0.16em]">WAVE ELEVEN</span>
              <span className={`mt-1 block text-[9px] uppercase tracking-label ${solid ? "text-ink/45" : "text-white/50"}`}>Digital Design Studio</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-9 lg:flex">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative text-[11px] uppercase tracking-label transition-opacity duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-full after:origin-left after:bg-champagne after:transition-transform after:duration-500 hover:after:scale-x-100 ${textCls} ${isActive ? "opacity-100 after:scale-x-100" : "opacity-70 hover:opacity-100 after:scale-x-0"}`}>
                {l.label}
              </NavLink>
            ))}
            <Btn to="/start" variant="outline" dark={!solid} testid="nav-cta" className="py-3 px-6">Start a Project</Btn>
          </nav>

          <button data-testid="mobile-menu-toggle" className={`lg:hidden ${textCls}`} onClick={() => setOpen(true)} aria-label="Open menu">
            <Menu size={26} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="mobile-menu" className="fixed inset-0 z-[70] flex flex-col bg-ink lg:hidden"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
            <div className="flex items-center justify-between px-6 py-5 text-white">
              <span className="flex items-center gap-3"><WaveMark width={42} /><span className="font-display text-lg font-bold tracking-[0.16em]">WAVE ELEVEN</span></span>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close menu"><X size={28} strokeWidth={1.5} /></button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-6">
              {LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.07 * i + 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
                  <Link to={l.to} data-testid={`mobile-link-${l.label.toLowerCase()}`} className="block border-b border-white/10 py-5 font-serif text-4xl text-white">{l.label}</Link>
                </motion.div>
              ))}
            </nav>
            <div className="px-6 pb-10"><Btn to="/start" variant="gold" className="w-full" testid="mobile-cta">Start a Project</Btn></div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
